import { Alert } from "@mui/lab";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { TimeService } from "../../../../../services";
import { Job } from "../../models/Job";
import { upsertJob } from "../../repositories/JobsRepository";

import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";

type Props = {
	job: Job | null;
	modal: "create" | "update" | "reset" | "enable" | "";
	onHide: ({ close, entityId }: { close: boolean; entityId?: number }) => void;
	suffixTitle?: string;
};

export const JobDetails = ({ job, modal, onHide, suffixTitle }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [error, setError] = useState("");
	const [openSnackbar, setOpenSnackbar] = useState(false);

	const succesfullyMessage = `The job was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		formState: { dirtyFields },
	} = useForm<Job>({
		mode: "onChange",
	});

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const updateJob = useMutation({
		mutationFn: upsertJob,
		onSuccess: async (id: number) => {
			setOpenSnackbar(true);
			onHide({ close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<Job> = async (data: Job) => {
		setIsSubmitLoading(true);
		if (modal !== "reset") {
			await updateJob.mutate(data);
		}
	};

	useEffect(() => {
		if (modal === "create" || modal === "update") {
			reset(
				job || {
					sProcedure: "",
					RestartAfterFailure: true,
					PeriodTime: null,
					StartTime: null,
					LastStatus: null,
					LastExecutionTime: null,
					LastExecutionResult: "",
					NextExecutionTime: null,
				},
			);
			setIsSubmitLoading(false);
		}
	}, [modal, job]);

	return (
		<>
			{(modal == "update" || modal == "create") && (
				<HelmetDexteel
					title={`${modal == "update" ? job?.sProcedure : "New Job"}${
						suffixTitle ? ` - ${suffixTitle}` : ""
					}`}
				/>
			)}
			{(job === null || modal !== "update") && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a job or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={2} alignItems="center">
						<Grid2 size={{ xs: 12, md: 12 }}>
							<Controller
								name="sProcedure"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label="Name"
										value={field.value ?? ""}
										onChange={field.onChange}
										variant="outlined"
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }} justifyContent="center">
							<Controller
								name="RestartAfterFailure"
								control={control}
								render={({ field }) => (
									<FormControlLabel
										control={
											<Checkbox
												checked={!!field.value}
												onChange={field.onChange}
												name="RestartAfterFailure"
												color="primary"
											/>
										}
										label="Restart After Failure"
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="PeriodTime"
								control={control}
								rules={{ required: "Period time is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label="Period Time (seconds)"
										value={field.value ?? ""}
										onChange={field.onChange}
										variant="outlined"
										size="small"
										margin="dense"
										fullWidth
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="StartTime"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<>
										<DateTimePicker
											label="Start Time"
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											format="MM/DD/YYYY HH:mm:ss"
											views={["year", "month", "day", "hours", "minutes"]}
											ampm={false}
											timezone={TimeService.getInstance().getServerTimeZone()}
											slotProps={{
												textField: {
													size: "small",
													margin: "none",
													error: !!error,
													fullWidth: true,
												},
											}}
										/>
									</>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LastStatus"
								control={control}
								render={({ field }) => (
									<TextField
										label="Last Status"
										value={field.value ?? ""}
										variant="outlined"
										size="small"
										fullWidth
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LastExecutionTime"
								control={control}
								render={({ field }) => (
									<DateTimePicker
										label="Last Execution Time"
										value={field.value ? moment(field.value) : null}
										format="MM/DD/YYYY HH:mm:ss"
										views={["year", "month", "day", "hours", "minutes"]}
										ampm={false}
										timezone={TimeService.getInstance().getServerTimeZone()}
										disabled
										slotProps={{
											textField: {
												size: "small",
												margin: "none",
												fullWidth: true,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="NextExecutionTime"
								control={control}
								render={({ field }) => (
									<DateTimePicker
										label="Next Execution Time"
										value={field.value ? moment(field.value) : null}
										format="MM/DD/YYYY HH:mm:ss"
										views={["year", "month", "day", "hours", "minutes"]}
										ampm={false}
										timezone={TimeService.getInstance().getServerTimeZone()}
										disabled
										slotProps={{
											textField: {
												size: "small",
												margin: "none",
												fullWidth: true,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 12 }}>
							<Controller
								name="LastExecutionResult"
								control={control}
								render={({ field }) => (
									<TextField
										label="Last Execution Result"
										value={field.value ?? ""}
										variant="outlined"
										size="small"
										margin="dense"
										fullWidth
										multiline
										minRows={2}
										maxRows={2}
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide({ close: true })}
								>
									Close
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading || !isFormDirty}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</form>
			)}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={() => setOpenSnackbar(false)}
			>
				<Alert severity="success" onClose={() => setOpenSnackbar(false)}>
					The job was updated succesfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
